import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import RecapService from '@/service/recap.service';

interface Host {
  id: string;
  name: string;
  description?: string;
}

@Component
export default class PodcastSetting extends Vue {
  recapService = new RecapService();

  @Prop({ type: String, required: true })
  readonly recapId!: string;

  @Prop({ type: String, required: true })
  readonly recapLang!: string;

  userId = '';
  requestId = '';
  paid = false;
  userName = '';
  emailAddress = '';
  phoneNumber = '';
  phoneCountry = '';
  langKey = '';
  podcastHosts: any[] = [];
  listPodcast = [];
  speakerNm = '';
  type = '';
  speakerRole = '';
  speakerImg = '';
  hosts: Host[] = [];
  selectedHost = '';
  progress = 30;
  isGenerating = false;
  isGenerated = false;
  isDarkMode = false;

  podcastEnabled = true;
  isPlaying = false;
  isMuted = false;
  playerProgress = 0;
  volume = 0;
  currentTime = 0;
  totalTime = 0;
  audio: HTMLAudioElement = new Audio('');
  date = null;
  currentHostDescription = '';

  async generate() {
    try {
      this.isGenerating = true;
      this.isGenerated = true;

      await this.savePodcastSetting();
      const generateResult = await this.generatePodcast();

      if (generateResult) {
        this.$emit('generation-completed', { startCall: true, checkShowPodcast: false });
      } else {
        this.$emit('generation-completed', { startCall: false, checkShowPodcast: true });
      }
    } catch (error) {
      console.error('Error generating podcast:', error);
    } finally {
      this.isGenerating = false;
    }
  }

  beforeDestroy() {
    this.audio.pause();
    this.audio.src = '';
    this.audio = null;
  }

  async mounted() {
    this.audio = new Audio();
    this.initializeUserData();
    await this.getListPodcast();
    await this.getNewPodcast(this.userId, this.recapId);

    if (this.podcastHosts.length > 0) {
      this.selectedHost = this.podcastHosts[0].narratorId;
    }
    if (this.podcastHosts.length > 0) {
      this.currentHostDescription = this.podcastHosts[0].narratorDescription;
      if (this.selectedHost) {
        const selectedHost = this.podcastHosts.find(host => host.narratorId === this.selectedHost);
        if (selectedHost) {
          this.speakerImg = selectedHost.narratorImg;
          this.currentHostDescription = selectedHost.narratorDescription;
          this.loadAudio(selectedHost.narratorUrlExample);
        }
      }
    }
  }

  private initializeUserData() {
    const userData = this.getLocalStorageUser();
    if (userData) {
      this.userId = userData.id;
      this.paid = userData.paid;
      this.langKey = userData.langKey;
      this.userName = userData.lastName;
      this.emailAddress = userData.email;
      this.phoneNumber = userData.phoneNumber;
      this.phoneCountry = userData.phoneCountry;
      if (userData.colorTheme === 'light' || userData.colorTheme === null) {
        this.isDarkMode = false;
      } else {
        this.isDarkMode = true;
      }
    }
  }

  private async savePodcastSetting() {
    this.requestId = this.generateRequestId();
    if (this.recapId == null) {
      return;
    }
    const now = new Date();
    const timezoneOffset = now.getTimezoneOffset(); // phút, với VN: -420
    const localTime = new Date(now.getTime() - timezoneOffset * 60000);
    const requestBody = {
      requestId: this.requestId,
      userId: this.userId,
      recapId: this.recapId,
      speakerId: this.selectedHost,
      role: this.speakerRole || 'host',
      createdAt: localTime,
      image: this.speakerImg,
      loadingData: false,
    };

    try {
      await this.recapService.savePodcastSetting(requestBody);
    } catch (error) {
      console.error('Error while saving podcast settings:', error);
    }
  }

  private buildPodcastRequestBody(): any {
    this.getSpeakerInfo(this.selectedHost);

    return {
      request_id: this.requestId,
      user_info: {
        uid: this.userId,
        user_name: this.userName,
        email_address: this.emailAddress,
        phone_number: this.phoneNumber,
        phone_country: this.phoneCountry,
      },
      recap_id: this.recapId,
      speakers: [
        {
          speaker_id: this.selectedHost,
          speaker_nm: this.speakerNm,
          type: this.speakerRole,
          speaker_role: this.speakerRole,
          tempo: 1.15,
        },
      ],
      created_date: this.date,
      language: this.recapLang,
    };
  }

  async editRecapPodcast(): Promise<any> {
    const requestBody = this.buildPodcastRequestBody();

    try {
      await this.recapService.editRecapPodcast(requestBody);
    } catch (error) {
      console.error('Failed to update podcast:', error);
    }
  }

  async generatePodcast(): Promise<any> {
    const requestBody = this.buildPodcastRequestBody();
    try {
      await this.recapService.generatePodcast(requestBody);
    } catch (error) {
      console.error('Failed to generate podcast:', error);
    }
  }

  private async getPodcast(userId: string, recapId: string, speakerId: string): Promise<any | null> {
    try {
      this.listPodcast = await this.recapService.getPodcast(userId, recapId, speakerId);
    } catch (error) {
      console.error('Error fetching recap settings:', error);
    }
  }

  private async getNewPodcast(userId: string, recapId: string): Promise<any | null> {
    try {
      const response = await this.recapService.getNewPodcast(userId, recapId);
      this.getSpeakerInfo(this.selectedHost);
      return response;
    } catch (error) {
      console.error('Error while retrieving recap settings:', error);
    }
  }

  private getLocalStorageUser(): any {
    try {
      const userData = localStorage.getItem('localStorageUser');
      return userData ? JSON.parse(userData) : null;
    } catch (e) {
      console.error('Error parsing user data:', e);
      return null;
    }
  }

  private async getListPodcast(): Promise<void> {
    try {
      let langCode = 'en';

      const lang = this.recapLang;
      if (lang === 'KR' || lang === 'KOREAN') {
        langCode = 'ko';
      } else if (lang === 'EN' || lang === 'ENGLISH') {
        langCode = 'en';
      }

      const response = await this.recapService.getListPodcast(langCode);
      this.podcastHosts = response.body;

      if (this.podcastHosts.length > 0 && !this.selectedHost) {
        this.selectedHost = this.podcastHosts[0].narratorId;
      }
    } catch (error) {
      console.error('Error while retrieving the podcast list:', error);
    }
  }

  onHostSelected(hostId: string) {
    this.selectedHost = hostId;
    const selectedHost = this.podcastHosts.find(host => host.narratorId === hostId);
    if (selectedHost) {
      this.speakerImg = selectedHost.narratorImg;
      this.currentHostDescription = selectedHost.narratorDescription;
      this.loadAudio(selectedHost.narratorUrlExample);
    }
  }

  currentHostIndex = 0;
  hostsPerPage = 4;

  get visibleHosts() {
    return this.podcastHosts.slice(this.currentHostIndex, this.currentHostIndex + this.hostsPerPage);
  }

  prevHosts() {
    if (this.currentHostIndex > 0) {
      this.currentHostIndex -= this.hostsPerPage;
      if (this.currentHostIndex < 0) this.currentHostIndex = 0;
    }
  }

  nextHosts() {
    if (this.currentHostIndex + this.hostsPerPage < this.podcastHosts.length) {
      this.currentHostIndex += this.hostsPerPage;
    }
  }

  getHostDescription(hostId: string): string {
    const host = this.hosts.find(h => h.id === hostId);
    return host?.description || '';
  }

  generateRequestId(length = 20): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let requestId = '';
    for (let i = 0; i < length; i++) {
      const randomIndex = Math.floor(Math.random() * chars.length);
      requestId += chars[randomIndex];
    }
    return requestId;
  }

  getSpeakerInfo(speakerId: string) {
    const host = this.podcastHosts.find(h => h.narratorId === speakerId);
    this.speakerNm = host?.narratorName || '';
    this.speakerRole = host?.narratorPosition || '';
    this.speakerImg = host?.narratorImg || '';
    return host || null;
  }

  formatTime(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes < 10 ? '0' : ''}${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  }

  skipBackward(): void {
    if (!this.podcastEnabled) return;
    this.audio.currentTime = Math.max(0, this.audio.currentTime - 10);
    this.updateProgress();
  }

  skipForward(): void {
    if (!this.podcastEnabled) return;
    this.audio.currentTime = Math.min(this.audio.duration, this.audio.currentTime + 10);
    this.updateProgress();
  }

  togglePlay(): void {
    if (this.isPlaying) {
      this.audio.pause();
    } else {
      this.audio.play().catch(error => {
        console.error('Error playing audio:', error);
      });
    }
    this.isPlaying = !this.isPlaying;
  }

  toggleMute(): void {
    this.isMuted = !this.isMuted;
    this.audio.muted = this.isMuted;
  }

  adjustVolume(): void {
    this.audio.volume = this.volume / 100;
  }

  onProgressChange(): void {
    this.audio.currentTime = (this.playerProgress / 100) * this.totalTime;
  }

  updateProgress(): void {
    this.playerProgress = (this.audio.currentTime / this.totalTime) * 100;
    this.currentTime = this.audio.currentTime;
  }

  loadAudio(url: string): void {
    if (this.isPlaying) {
      this.audio.pause();
      this.isPlaying = false;
    }

    this.audio.src = url;
    this.audio.load();

    this.audio.onloadedmetadata = () => {
      this.totalTime = this.audio.duration;
    };

    this.audio.ontimeupdate = this.updateProgress;

    this.audio.onended = () => {
      this.isPlaying = false;
      this.playerProgress = 0;
      this.currentTime = 0;
    };
  }
}
